import VueRouter, { NavigationGuardNext, Route } from "vue-router"

import Dashboard from "../views/Dashboard.vue"
import Login from "../views/Login.vue"
import SignUp from "../views/Signup.vue"
import Account from "../views/Account.vue"
import Search from "../views/Search.vue"
import UserProfile from "../views/UserProfile.vue"
import Organization from "../views/Organization.vue"
import { attemptRedirectToNewInterface, attemptRedirectToOldInterface, hasPermission,
	hasPermissionToEditData, setApiCacheSkip } from "./guards"
import { Breadcrumb } from "@/types/breadcrumb"

import api from "@/services/api"
import { utils } from "@/mixins/utils"
import { Store } from "vuex"
import { BaseState } from "@/store"
import { EditDataPermissionTitles, EditDataPermission } from "@/types/permission"
import { DataSource } from "@/types"

/*
 * The router definition
 * Meta allows you to define how the route will look including:
 *  - The page navigation breadcrumb as static text
 *  - The page icon
 *  - The page navigation breadcrumb as a function allowing customisation of breadcrumbs
 *  - The old/new page to redirect to
 * NOTE: There is are 2 global router guards for redirecting users to the new/old interface as appropriate
 * NOTE: If you need to have a specific permission to view a route, use the hasPermission guard
 */

const routes = [
	{
		path: "/",
		name: "home",
		meta: {
			icon: "fa-home"
		},
		redirect: "/login"
	},
	{
		path: "/login",
		name: "login",
		component: Login,
		meta: {
			allowAnon: true,
			forceTheme: true,
			v1Only: true
		}
	},
	{
		path: "/signup",
		name: "signup",
		component: SignUp,
		meta: {
			allowAnon: true,
			icon: "fas fa-user-plus",
			breadcrumb: "Signup"
		}
	},
	{
		path: "/dashboard",
		name: "dashboard",
		component: () => import("@/views/Dashboard.vue"),
		meta: {
			icon: "fas fa-tachometer-alt",
			breadcrumb: "Dashboard",
			v1Only: false,
			allowApiCache: true
		}
	},
	{
		path: "/cases",
		name: "overview",
		component: () => import("@/views/CasesOverview.vue"),
		meta: {
			icon: "fa-newspaper",
			breadcrumb: "Overview",
			v2PathName: "v2-cases",
			allowApiCache: true
		}
	},
	{
		path: "/cases/:caseNumber",
		name: "case",
		component: () => import("@/views/CaseDetails.vue"),
		meta: {
			breadcrumb: "Case",
			v2PathName: "v2-case-with-number",
			allowApiCache: true
		}
	},
	{
		path: "/cases/restricted/:caseNumber",
		name: "restricted-cases",
		component: () => import("@/views/RestrictedCaseDetails.vue"),
		meta: {
			breadcrumb: "Restricted Cases",
			allowApiCache: true
		}
	},
	{
		path: "/v2/cases",
		name: "v2-cases",
		redirect: "/v2/cases/overview",
		component: () => import("@/views/new/Cases.vue"),
		meta: {
			breadcrumb: "Records & Cases",
			breadcrumbTransform: (_route: Route, breadcrumbItem: Breadcrumb, store: Store<BaseState>): Breadcrumb | Breadcrumb[] => {
				// If the name for records and cases are the same, then don't show both
				breadcrumbItem.text = store.state.recordName === store.state.caseName
					? utils.methods.pluralize(store.state?.me?.recordName ?? "Record")
					: `${utils.methods.pluralize(store.state?.me?.recordName ?? "Record")} & ${utils.methods.pluralize(store.state?.me?.caseName ?? "Case")}`
				return breadcrumbItem
			},
			allowApiCache: true
		},
		children: [
			{
				path: "overview/:viewId?",
				name: "v2-cases-overview",
				component: () => import("@/components/New/Cases/Overview.vue"),
				meta: {
					oldPathName: "overview",
					breadcrumb: "Overview",
					breadcrumbTransform: async (route: Route, breadcrumbItem: Breadcrumb, store: any): Promise<Breadcrumb | Breadcrumb[]> => {
						// Displays the breadcrumb as Records & Cases / [Folder name]? / Case View name
						if (!route.params?.viewId) {
							return breadcrumbItem
						}

						if (store.state?.cases?.viewsService) {
							store.state.cases.viewsService.activeViewId = route.params.viewId
							await store.state.cases.viewsService.loadViews()
						}

						const view = store.state.cases?.viewsService?.activeView

						if (view) {
							// If we have the view, replace the breadcrumb text (the view guid) with the actual title.
							breadcrumbItem.text = view.title

							if (view.folder) {
								// If the view has a folder, create a new breadcrumb for it.
								const folderBreadcrumb: Breadcrumb = {
									text: view.folder
								}

								return [folderBreadcrumb, breadcrumbItem]
							}
						}

						return breadcrumbItem
					},
					allowApiCache: true
				}
			},
			{
				path: ":caseNumber",
				name: "v2-case-with-number",
				component: () => import("@/components/New/Cases/CaseDetails.vue"),
				meta: {
					oldPathName: "case",
					breadcrumb: "Case Details",
					breadcrumbTransform: (route: Route, breadcrumbItem: Breadcrumb, store: Store<BaseState>): Breadcrumb | Breadcrumb[] => {
						// Displays the breadcrumb as case number or New
						breadcrumbItem.text = !!route.params["caseNumber"] && route.params["caseNumber"].toLowerCase() === "new"
							? "New"
							: `${store.state.caseName} #${route.params["caseNumber"]}`

						return breadcrumbItem
					},
					allowApiCache: true
				}
			}
		]
	},
	{
		path: "/account",
		name: "account",
		component: Account,
		meta: {
			breadcrumb: "Account"
		}
	},
	{
		path: "/configuration",
		name: "configuration",
		component: () => import("@/views/Configuration.vue"),
		beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => hasPermission(to, from, next, "editData"),
		meta: {
			icon: "fa-cog",
			breadcrumb: "Configuration"
		},
		children: [
			...EditDataPermissionTitles
				.filter(permission => permission.component)
				.map(permission => {
					return {
						path: permission.path!,
						name: permission.title,
						component: permission.component,
						beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => hasPermissionToEditData(to, from, next, permission.id),
						meta: {
							breadcrumb: permission.title,
							breadcrumbTransform: permission.id === EditDataPermission.DataSourceList
								? async (_route: Route, breadcrumbItem: Breadcrumb, store: Store<BaseState>): Promise<Breadcrumb> => {
									const dataSourceId = _route.params.dataSourceId
									const setup = (store.state as any).setup

									if (!setup.dataSources) {
										await store.dispatch("setup/loadDataSources")
									}

									if (setup.dataSources) {
										const selected = setup.dataSources.find((d: DataSource) => d.id === dataSourceId)
										breadcrumbItem.text = selected?.title ?? "Data source"
									} else {
										breadcrumbItem.text = "Data source"
									}

									return breadcrumbItem
								}
								: null
						}
					}
				}),
			{
				id: "AuditLog",
				name: "Change History",
				path: "history",
				component: () => import("@/components/Config/Audit/AuditView.vue")
			},
			{
				path: "admin",
				name: "Admin Settings",
				beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => hasPermission(to, from, next, "isAdmin"),
				component: () => import("@/components/Config/TenantSettings/TenantSettingsPage.vue")
			}
		]
	},
	{
		path: "/users",
		name: "users",
		component: () => import("@/views/Users.vue"),
		beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => hasPermission(to, from, next, "editUsers"),
		meta: {
			icon: "fa-cog",
			breadcrumb: "Users",
			allowApiCache: true
		}
	},
	{
		path: "/insights",
		name: "insights",
		component: () => import("@/views/Insights.vue"),
		meta: {
			icon: "fa-cog",
			breadcrumb: "Insights",
			allowApiCache: true
		}
	},
	{
		path: "/integrations",
		name: "integrations",
		component: () => import("@/views/Integrations.vue"),
		meta: {
			icon: "fa-cog",
			breadcrumb: "Integrations",
			allowApiCache: true
		}
	},
	{
		path: "/scheduling",
		name: "scheduling",
		component: () => import("@/views/Scheduling.vue"),
		beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => hasPermission(to, from, next, "scheduling"),
		meta: {
			icon: "fa-calendar",
			breadcrumb: "Scheduling",
			allowApiCache: true
		}
	},
	{
		path: "/userprofile/:userId",
		name: "userprofile",
		component: UserProfile,
		meta: {
			icon: "fa-cog",
			breadcrumbTransform: async (route: Route, breadcrumbItem: Breadcrumb, _store: Store<BaseState>): Promise<Breadcrumb | Breadcrumb[]> => {
				// Displays the breadcrumb as the name of the user
				let user = await api.userDetails(route.params["userId"])

				return [breadcrumbItem, { text: user.name }]
			},
			breadcrumb: "User Profile",
			allowApiCache: true
		}
	},
	{
		path: "/organization",
		name: "organization",
		component: Organization,
		meta: {
			icon: "fa-cog",
			breadcrumb: "Organization"
		}
	},
	{
		path: "/search",
		name: "search",
		component: Search,
		meta: {
			icon: "fa-cog",
			breadcrumb: "Search",
			allowApiCache: true
		}
	},
	{
		path: "/system",
		name: "system",
		component: () => import("@/views/System.vue"),
		meta: {
			icon: "fa-cog",
			breadcrumb: "System"
		}
	},
	{
		path: "/reportview/:reportDataId?",
		name: "reportview",
		component: () => import("@/views/ReportView.vue"),
		meta: {
			icon: "fa-home",
			allowAnon: true,
			allowApiCache: true
		}
	},
	{
		// handle a non-existing route - redirect to root route
		path: "*",
		redirect: "/login"
	}
]

const router = new VueRouter({
	base: process.env.BASE_URL,
	routes
})

// Register global navigation guards
router.beforeEach(attemptRedirectToNewInterface)
router.beforeEach(attemptRedirectToOldInterface)
router.beforeEach(setApiCacheSkip)

export default router
